/**
 * Dual-pane zone assignment for the Create/Edit Tenant dialog.
 *
 * Replaces a flat checkbox list that had no notion of who else owned a zone.
 * prod.tenant_zones is constrained UNIQUE (tenant_id, zone_id) — which blocks a
 * duplicate *pair*, not a zone held by two tenants — and set_tenant_zones_for_admin
 * scopes every read and write by tenant_id, so it is structurally incapable of
 * noticing a zone already belongs to someone else. Six zones were double-allocated
 * in production before this was cleaned up.
 *
 * Rather than validate on save, zones owned elsewhere are rendered in a read-only
 * section with no move control at all: the invalid state is unreachable instead of
 * rejected late.
 */
/* eslint-disable max-lines-per-function, complexity --
   Same allowance TenantSetupTab takes. A listbox that meets the ARIA APG pattern
   carries its keyboard map, selection state, filtering and grouping in one
   component; splitting those across 50-line functions would scatter tightly
   coupled state without making any of it clearer. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Info,
  Lock,
  MapPin,
  Search
} from 'lucide-react';
import { Badge } from '@vehix/portal/components/ui/badge';
import { Button } from '@vehix/portal/components/ui/button';
import { Input } from '@vehix/portal/components/ui/input';
import { Skeleton } from '@vehix/portal/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@vehix/portal/components/ui/table';
import { cn } from '@vehix/portal/lib/utils';
import { getOemTheme } from '../utils/oemColors';
import { getZoneSizeTier, getZoneSizeTierShortLabel } from '../utils/zoneSizeThresholds';

/** A zone as this panel needs it — a structural subset of the Zone interface. */
export interface AssignableZone {
  id: string;
  zone_name: string;
  zone_slug?: string | undefined;
  oem: string;
  /**
   * The stored tier column. Kept on the type because the query still selects it, but
   * deliberately NOT rendered — see the size chip in ZoneRow. Nothing recomputes it, so
   * it disagrees with the live count on roughly half of all zones.
   */
  zone_size?: string | undefined;
  total_recalls_current?: number | undefined;
}

export interface ZoneOwner {
  id: string;
  tenant_name: string;
}

export interface ZoneAssignmentPanelProps {
  /** Zones this tenant may choose from — already narrowed to its OEMs. */
  zones: AssignableZone[];
  /** Currently selected zone ids (the pending form value). */
  selectedIds: string[];
  onChange: (zoneIds: string[]) => void;
  /** zone_id -> every tenant that owns it, from the saved tenant list. */
  zoneOwners: Map<string, ZoneOwner[]>;
  /** Tenant being edited; undefined when creating. */
  editingTenantId?: string | undefined;
  isLoading?: boolean | undefined;
  /** Group rows by OEM — worth it only when the tenant spans several. */
  showOemGroups?: boolean | undefined;
  /** zone_id -> ISO timestamp; drives the "Assigned since" column. */
  assignmentDates?: Record<string, string> | undefined;
}

function formatRecalls(n: number | undefined): string {
  if (n === undefined || n === null) return '—';
  return n.toLocaleString();
}

/** Case-insensitive match across the fields an admin would actually type. */
function matches(zone: AssignableZone, term: string): boolean {
  if (!term) return true;
  const t = term.trim().toLowerCase();
  if (!t) return true;
  return (
    zone.zone_name.toLowerCase().includes(t) ||
    zone.oem.toLowerCase().includes(t) ||
    (zone.zone_slug ?? '').toLowerCase().includes(t)
  );
}

/** Stable ordering: OEM, then zone name. Keeps both panes predictable. */
function byOemThenName(a: AssignableZone, b: AssignableZone): number {
  if (a.oem !== b.oem) return a.oem.localeCompare(b.oem);
  return a.zone_name.localeCompare(b.zone_name);
}

type SortKey = 'zone' | 'tenant' | 'since';

interface BlockedRow {
  zone: AssignableZone;
  owner: ZoneOwner;
  assignedAt?: string | undefined;
}

/**
 * Render an assignment timestamp as a plain date.
 *
 * Falls back to an em dash rather than a guess: assignment dates come from
 * prod.tenant_zones.created_at, which the fetch treats as non-fatal, so an
 * unavailable value means "not loaded", not "assigned long ago".
 */
function formatAssignedSince(iso: string | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
}

export function ZoneAssignmentPanel({
  zones,
  selectedIds,
  onChange,
  zoneOwners,
  editingTenantId,
  isLoading = false,
  showOemGroups = false,
  assignmentDates
}: ZoneAssignmentPanelProps) {
  const [availableSearch, setAvailableSearch] = useState('');
  const [assignedSearch, setAssignedSearch] = useState('');
  const [availableChecked, setAvailableChecked] = useState<string[]>([]);
  const [assignedChecked, setAssignedChecked] = useState<string[]>([]);
  const [blockedOpen, setBlockedOpen] = useState(false);
  /** Screen-reader announcement for the last move. */
  const [announcement, setAnnouncement] = useState('');

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  /**
   * Split every zone into the four states.
   *
   * `zoneOwners` reflects *saved* ownership while `selectedIds` is the pending
   * form value, so a zone the admin just added reads as assigned even though no
   * tenant_zones row exists for it yet — which is what we want.
   */
  const { available, assigned, blocked } = useMemo(() => {
    const av: AssignableZone[] = [];
    const asg: AssignableZone[] = [];
    const blk: { zone: AssignableZone; owners: ZoneOwner[] }[] = [];

    for (const zone of [...zones].sort(byOemThenName)) {
      const owners = zoneOwners.get(zone.id) ?? [];
      const others = owners.filter(o => o.id !== editingTenantId);
      if (selectedSet.has(zone.id)) {
        asg.push(zone);
      } else if (others.length > 0) {
        blk.push({ zone, owners: others });
      } else {
        av.push(zone);
      }
    }
    return { available: av, assigned: asg, blocked: blk };
  }, [zones, zoneOwners, editingTenantId, selectedSet]);

  /** Assigned zones that another tenant also holds — drift, not a normal state. */
  const conflicted = useMemo(() => {
    const out = new Map<string, ZoneOwner[]>();
    for (const zone of assigned) {
      const others = (zoneOwners.get(zone.id) ?? []).filter(o => o.id !== editingTenantId);
      if (others.length > 0) out.set(zone.id, others);
    }
    return out;
  }, [assigned, zoneOwners, editingTenantId]);

  const visibleAvailable = useMemo(
    () => available.filter(z => matches(z, availableSearch)),
    [available, availableSearch]
  );
  const visibleAssigned = useMemo(
    () => assigned.filter(z => matches(z, assignedSearch)),
    [assigned, assignedSearch]
  );

  // Drop checks for rows that have moved or been filtered away, so "move
  // selected" can never act on something the admin can no longer see.
  // Returning `prev` unchanged when nothing was dropped matters: filter() always
  // allocates, and handing React a fresh array every time would re-render on
  // every keystroke in the search box.
  useEffect(() => {
    const ids = new Set(visibleAvailable.map(z => z.id));
    setAvailableChecked(prev => {
      const next = prev.filter(id => ids.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [visibleAvailable]);
  useEffect(() => {
    const ids = new Set(visibleAssigned.map(z => z.id));
    setAssignedChecked(prev => {
      const next = prev.filter(id => ids.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [visibleAssigned]);

  const announce = (moved: number, direction: 'assigned' | 'available', total: number) => {
    const noun = moved === 1 ? 'zone' : 'zones';
    setAnnouncement(`${moved} ${noun} moved to ${direction}. ${total} zones assigned.`);
  };

  const assign = (ids: string[]) => {
    if (ids.length === 0) return;
    const next = [...selectedIds, ...ids.filter(id => !selectedSet.has(id))];
    onChange(next);
    setAvailableChecked([]);
    announce(ids.length, 'assigned', next.length);
  };

  const unassign = (ids: string[]) => {
    if (ids.length === 0) return;
    const drop = new Set(ids);
    const next = selectedIds.filter(id => !drop.has(id));
    onChange(next);
    setAssignedChecked([]);
    announce(ids.length, 'available', next.length);
  };

  return (
    <div className='space-y-3'>
      {/* Announcements are the only way a screen-reader user learns a move
          landed — the visual cue is a row jumping panes. */}
      <div aria-live='polite' aria-atomic='true' className='sr-only'>
        {announcement}
      </div>

      <div className='grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-stretch'>
        <ZonePane
          title='Available'
          paneId='zones-available'
          zones={visibleAvailable}
          totalCount={available.length}
          checkedIds={availableChecked}
          onCheckedChange={setAvailableChecked}
          search={availableSearch}
          onSearchChange={setAvailableSearch}
          onMoveSelection={ids => assign(ids)}
          moveDirection='right'
          isLoading={isLoading}
          showOemGroups={showOemGroups}
          emptyAll='No zones available for the selected OEM(s).'
          emptyFiltered='No available zone matches your search.'
          onClearSearch={() => setAvailableSearch('')}
        />

        <MoveControls
          canAssign={availableChecked.length > 0}
          canUnassign={assignedChecked.length > 0}
          canAssignAll={visibleAvailable.length > 0}
          canUnassignAll={visibleAssigned.length > 0}
          assignCount={availableChecked.length}
          unassignCount={assignedChecked.length}
          assignAllCount={visibleAvailable.length}
          unassignAllCount={visibleAssigned.length}
          onAssign={() => assign(availableChecked)}
          onUnassign={() => unassign(assignedChecked)}
          onAssignAll={() => assign(visibleAvailable.map(z => z.id))}
          onUnassignAll={() => unassign(visibleAssigned.map(z => z.id))}
        />

        <ZonePane
          title='Assigned'
          paneId='zones-assigned'
          zones={visibleAssigned}
          totalCount={assigned.length}
          checkedIds={assignedChecked}
          onCheckedChange={setAssignedChecked}
          search={assignedSearch}
          onSearchChange={setAssignedSearch}
          onMoveSelection={ids => unassign(ids)}
          moveDirection='left'
          isLoading={isLoading}
          showOemGroups={showOemGroups}
          conflicted={conflicted}
          emptyAll='No zones assigned yet. Move zones across from Available.'
          emptyFiltered='No assigned zone matches your search.'
          onClearSearch={() => setAssignedSearch('')}
        />
      </div>

      {blocked.length > 0 && (
        <BlockedSection
          entries={blocked}
          open={blockedOpen}
          onToggle={() => setBlockedOpen(o => !o)}
          assignmentDates={assignmentDates}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Move controls                                                       */
/* ------------------------------------------------------------------ */

interface MoveControlsProps {
  canAssign: boolean;
  canUnassign: boolean;
  canAssignAll: boolean;
  canUnassignAll: boolean;
  assignCount: number;
  unassignCount: number;
  assignAllCount: number;
  unassignAllCount: number;
  onAssign: () => void;
  onUnassign: () => void;
  onAssignAll: () => void;
  onUnassignAll: () => void;
}

function MoveControls({
  canAssign,
  canUnassign,
  canAssignAll,
  canUnassignAll,
  assignCount,
  unassignCount,
  assignAllCount,
  unassignAllCount,
  onAssign,
  onUnassign,
  onAssignAll,
  onUnassignAll
}: MoveControlsProps) {
  return (
    <div className='flex md:flex-col items-center justify-center gap-2 md:px-1'>
      <Button
        type='button'
        variant='outline'
        size='icon'
        onClick={onAssign}
        disabled={!canAssign}
        aria-label={`Assign ${assignCount} selected zone(s)`}
        title='Assign selected'
      >
        <ChevronRight className='h-4 w-4' />
      </Button>
      <Button
        type='button'
        variant='outline'
        size='icon'
        onClick={onUnassign}
        disabled={!canUnassign}
        aria-label={`Unassign ${unassignCount} selected zone(s)`}
        title='Unassign selected'
      >
        <ChevronLeft className='h-4 w-4' />
      </Button>
      <Button
        type='button'
        variant='ghost'
        size='icon'
        onClick={onAssignAll}
        disabled={!canAssignAll}
        aria-label={`Assign all ${assignAllCount} shown zone(s)`}
        title='Assign all shown'
      >
        <ChevronsRight className='h-4 w-4' />
      </Button>
      <Button
        type='button'
        variant='ghost'
        size='icon'
        onClick={onUnassignAll}
        disabled={!canUnassignAll}
        aria-label={`Unassign all ${unassignAllCount} shown zone(s)`}
        title='Unassign all shown'
      >
        <ChevronsLeft className='h-4 w-4' />
      </Button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* A single pane                                                       */
/* ------------------------------------------------------------------ */

interface ZonePaneProps {
  title: string;
  paneId: string;
  zones: AssignableZone[];
  totalCount: number;
  checkedIds: string[];
  onCheckedChange: (ids: string[]) => void;
  search: string;
  onSearchChange: (v: string) => void;
  onMoveSelection: (ids: string[]) => void;
  moveDirection: 'left' | 'right';
  isLoading: boolean;
  showOemGroups: boolean;
  conflicted?: Map<string, ZoneOwner[]> | undefined;
  emptyAll: string;
  emptyFiltered: string;
  onClearSearch: () => void;
}

function ZonePane({
  title,
  paneId,
  zones,
  totalCount,
  checkedIds,
  onCheckedChange,
  search,
  onSearchChange,
  onMoveSelection,
  moveDirection,
  isLoading,
  showOemGroups,
  conflicted,
  emptyAll,
  emptyFiltered,
  onClearSearch
}: ZonePaneProps) {
  const headingId = `${paneId}-heading`;
  const listRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState<string | null>(null);

  const checkedSet = useMemo(() => new Set(checkedIds), [checkedIds]);
  /** Anchor for Shift+click range selection. */
  const anchorRef = useRef<string | null>(null);

  // Keep the active descendant on a row that still exists.
  useEffect(() => {
    if (activeId && !zones.some(z => z.id === activeId)) {
      setActiveId(zones[0]?.id ?? null);
    }
  }, [zones, activeId]);

  /**
   * Toggle a row's selection; Shift extends from the last row touched.
   *
   * Plain click is additive rather than replacing the selection. This control
   * replaced a checkbox list, so people arrive expecting clicks to accumulate,
   * and a replacing click would silently discard a multi-row selection built up
   * one click at a time.
   */
  const toggle = (id: string, opts: { shift?: boolean } = {}) => {
    const index = zones.findIndex(z => z.id === id);
    if (opts.shift && anchorRef.current) {
      const anchorIndex = zones.findIndex(z => z.id === anchorRef.current);
      if (anchorIndex !== -1 && index !== -1) {
        const [lo, hi] = anchorIndex < index ? [anchorIndex, index] : [index, anchorIndex];
        const range = zones.slice(lo, hi + 1).map(z => z.id);
        onCheckedChange(Array.from(new Set([...checkedIds, ...range])));
        return;
      }
    }
    anchorRef.current = id;
    onCheckedChange(checkedSet.has(id) ? checkedIds.filter(c => c !== id) : [...checkedIds, id]);
  };

  const moveActive = () => {
    // Prefer an explicit multi-selection; fall back to the focused row so a
    // keyboard user never has to select before moving.
    const ids = checkedIds.length > 0 ? checkedIds : activeId ? [activeId] : [];
    if (ids.length === 0) return;

    // Focus should land on the neighbour that survives the move, not on <body>.
    const firstIdx = zones.findIndex(z => z.id === ids[0]);
    const survivor = zones.slice(firstIdx + 1).find(z => !ids.includes(z.id));
    const fallback = zones
      .slice(0, firstIdx)
      .reverse()
      .find(z => !ids.includes(z.id));
    setActiveId(survivor?.id ?? fallback?.id ?? null);

    onMoveSelection(ids);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (zones.length === 0) return;
    const idx = activeId ? zones.findIndex(z => z.id === activeId) : -1;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      const nextIdx = Math.min(Math.max(idx + delta, 0), zones.length - 1);
      const next = zones[nextIdx === -1 ? 0 : nextIdx];
      if (next) {
        setActiveId(next.id);
        listRef.current
          ?.querySelector(`[data-zone-id="${next.id}"]`)
          ?.scrollIntoView({ block: 'nearest' });
      }
      return;
    }
    if (e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault();
      if (activeId) toggle(activeId);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      moveActive();
      return;
    }
    if (
      (moveDirection === 'right' && e.key === 'ArrowRight') ||
      (moveDirection === 'left' && e.key === 'ArrowLeft')
    ) {
      e.preventDefault();
      moveActive();
    }
  };

  const showEmpty = !isLoading && zones.length === 0;

  return (
    <div className='rounded-md border flex flex-col min-h-[320px]'>
      <div className='flex items-center justify-between gap-2 border-b px-3 py-2'>
        <h3 id={headingId} className='text-sm font-medium'>
          {title}
        </h3>
        <Badge variant='secondary' className='tabular-nums'>
          {totalCount}
        </Badge>
      </div>

      <div className='border-b p-2'>
        <div className='relative'>
          <Search
            className='absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2
              text-muted-foreground'
          />
          <Input
            value={search}
            onChange={e => onSearchChange(e.target.value)}
            placeholder={`Search ${title.toLowerCase()}…`}
            className='h-8 pl-7'
            aria-label={`Search ${title.toLowerCase()} zones`}
          />
        </div>
      </div>

      <div
        ref={listRef}
        role='listbox'
        aria-multiselectable='true'
        aria-labelledby={headingId}
        aria-activedescendant={activeId ? `${paneId}-opt-${activeId}` : undefined}
        tabIndex={0}
        onKeyDown={onKeyDown}
        className={cn(
          'flex-1 overflow-y-auto p-1 outline-none',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset'
        )}
        style={{ maxHeight: 260 }}
      >
        {isLoading && (
          <div className='space-y-1 p-1'>
            {[0, 1, 2, 3, 4].map(i => (
              <Skeleton key={i} className='h-11 w-full' />
            ))}
          </div>
        )}

        {showEmpty && (
          <div className='px-3 py-8 text-center text-sm text-muted-foreground'>
            <MapPin className='mx-auto mb-2 h-8 w-8 opacity-40' />
            {search.trim() ? (
              <>
                <p>{emptyFiltered}</p>
                <Button
                  type='button'
                  variant='link'
                  size='sm'
                  className='mt-1 h-auto p-0'
                  onClick={onClearSearch}
                >
                  Clear search
                </Button>
              </>
            ) : (
              <p>{emptyAll}</p>
            )}
          </div>
        )}

        {!isLoading &&
          zones.map((zone, i) => {
            const prev = zones[i - 1];
            const startsGroup = showOemGroups && (!prev || prev.oem !== zone.oem);
            return (
              <React.Fragment key={zone.id}>
                {startsGroup && (
                  <div
                    className='sticky top-0 z-10 bg-background/95 px-2 py-1 text-xs
                      font-medium text-muted-foreground backdrop-blur'
                  >
                    {zone.oem}
                  </div>
                )}
                <ZoneRow
                  zone={zone}
                  optionId={`${paneId}-opt-${zone.id}`}
                  checked={checkedSet.has(zone.id)}
                  active={activeId === zone.id}
                  conflictOwners={conflicted?.get(zone.id)}
                  showOem={!showOemGroups}
                  onToggle={e => toggle(zone.id, { shift: e.shiftKey })}
                  onActivate={() => setActiveId(zone.id)}
                  onMove={() => {
                    setActiveId(zone.id);
                    onMoveSelection([zone.id]);
                  }}
                />
              </React.Fragment>
            );
          })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* One zone row                                                        */
/* ------------------------------------------------------------------ */

interface ZoneRowProps {
  zone: AssignableZone;
  optionId: string;
  checked: boolean;
  active: boolean;
  conflictOwners?: ZoneOwner[] | undefined;
  showOem: boolean;
  onToggle: (e: React.MouseEvent) => void;
  onActivate: () => void;
  onMove: () => void;
}

function ZoneRow({
  zone,
  optionId,
  checked,
  active,
  conflictOwners,
  showOem,
  onToggle,
  onActivate,
  onMove
}: ZoneRowProps) {
  const theme = getOemTheme(zone.oem);
  // The hover title is qualified by OEM rather than slug. Zone names repeat heavily — 34
  // names are shared by two or more active zones, "Tampa" by six — and every such set
  // differs ONLY by OEM; the slugs are identical too, so `${name} (${slug})` disambiguated
  // nothing. On screen the OEM badge (or the OEM group header) already does this job.
  return (
    // jsx-a11y wants a key handler and tabIndex on any clickable element, but
    // that is wrong for this pattern. Per the ARIA APG listbox pattern the
    // LISTBOX holds focus and owns all keyboard handling, pointing at the
    // current row with aria-activedescendant; options must NOT be individually
    // focusable. Adding tabIndex here would put every zone in the tab order and
    // break arrow-key navigation. Keyboard support lives in ZonePane.onKeyDown.
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events,
       jsx-a11y/interactive-supports-focus */
    <div
      id={optionId}
      data-zone-id={zone.id}
      role='option'
      aria-selected={checked}
      onClick={e => {
        onActivate();
        onToggle(e);
      }}
      onDoubleClick={onMove}
      title={`${zone.zone_name} · ${zone.oem}`}
      className={cn(
        // 44px min target — comfortably clears WCAG 2.5.8 and touch guidance.
        'flex min-h-[44px] cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5',
        'text-sm hover:bg-accent/60',
        checked && 'bg-accent',
        active && 'ring-1 ring-inset ring-ring'
      )}
    >
      <span className={cn('h-2 w-2 flex-shrink-0 rounded-full', theme.dotClassName)} />
      <span className='flex-1 truncate font-medium'>{zone.zone_name}</span>

      {conflictOwners && conflictOwners.length > 0 && (
        <Badge variant='destructive' className='flex-shrink-0 gap-1 text-xs'>
          <AlertTriangle className='h-3 w-3' />
          also {conflictOwners.map(o => o.tenant_name).join(', ')}
        </Badge>
      )}

      {showOem && (
        <Badge variant='outline' className={cn('flex-shrink-0 text-xs', theme.className)}>
          {zone.oem}
        </Badge>
      )}

      {/* Derived from the count beside it, not from zone.zone_size. The stored column is
          whatever was passed at zone creation and nothing ever recomputes it, so it
          disagreed with the adjacent number on 137 of 248 zones — including zones stored
          'small' holding 780 recalls. Showing "S" next to "780" in the panel an admin uses
          to assign zones is worse than showing nothing.
          The count is prod.zones.total_recalls_current (the nightly stored value, now
          itself tenant-scoped). This panel has no live-RPC override, so it can lag the
          Zones list by up to a day — acceptable for picking zones, and the tier follows
          whatever number is actually displayed. */}
      {zone.total_recalls_current != null && (
        <span className='w-6 flex-shrink-0 text-center text-xs text-muted-foreground'>
          {getZoneSizeTierShortLabel(getZoneSizeTier(zone.total_recalls_current, zone.oem))}
        </span>
      )}
      <span className='w-14 flex-shrink-0 text-right text-xs tabular-nums text-muted-foreground'>
        {formatRecalls(zone.total_recalls_current)}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Zones owned by another tenant — read-only                           */
/* ------------------------------------------------------------------ */

interface BlockedSectionProps {
  entries: { zone: AssignableZone; owners: ZoneOwner[] }[];
  open: boolean;
  onToggle: () => void;
  /** zone_id -> ISO timestamp of when it was assigned to its current tenant. */
  assignmentDates?: Record<string, string> | undefined;
}

/**
 * Zones held by other tenants — a read-only, sortable table.
 *
 * Routinely the biggest of the three lists: a typical Ford dealer sees ~57
 * blocked zones against 33 available. Rendered flat and unbounded it overflowed
 * the dialog — DialogContent is deliberately overflow-visible so the OEM popover
 * can overhang, so it has no height cap of its own — and pushed the footer
 * buttons off screen.
 *
 * Three things make ~57 rows navigable: a bounded scroll area so the dialog
 * height never changes, a sticky header, and click-to-sort. Sorting by tenant
 * (the default) groups the table by owner, which is the unit the admin acts on:
 * "go unassign this from Lakeland Automall".
 */
function BlockedSection({ entries, open, onToggle, assignmentDates }: BlockedSectionProps) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({
    key: 'tenant',
    dir: 'asc'
  });

  const noun = entries.length === 1 ? 'zone is' : 'zones are';

  // One row per zone/owner pair. A zone with several owners (drift the unique
  // index will make impossible) appears once per owner.
  const rows = useMemo(() => {
    const out: BlockedRow[] = [];
    for (const { zone, owners } of entries) {
      for (const owner of owners) {
        out.push({ zone, owner, assignedAt: assignmentDates?.[zone.id] });
      }
    }
    return out;
  }, [entries, assignmentDates]);

  const term = search.trim().toLowerCase();

  const visibleRows = useMemo(() => {
    const matchesRow = (r: BlockedRow) =>
      r.zone.zone_name.toLowerCase().includes(term) ||
      r.owner.tenant_name.toLowerCase().includes(term) ||
      r.zone.oem.toLowerCase().includes(term);
    const filtered = term ? rows.filter(matchesRow) : rows;
    return [...filtered].sort((a, b) => {
      const factor = sort.dir === 'asc' ? 1 : -1;
      if (sort.key === 'zone') return factor * a.zone.zone_name.localeCompare(b.zone.zone_name);
      if (sort.key === 'since') {
        // Undated rows sort last regardless of direction — they are missing
        // data, not "oldest".
        if (!a.assignedAt) return 1;
        if (!b.assignedAt) return -1;
        return factor * a.assignedAt.localeCompare(b.assignedAt);
      }
      return (
        factor * a.owner.tenant_name.localeCompare(b.owner.tenant_name) ||
        a.zone.zone_name.localeCompare(b.zone.zone_name)
      );
    });
  }, [rows, term, sort]);

  const toggleSort = (key: SortKey) =>
    setSort(prev => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));

  const sortIndicator = (key: SortKey) => {
    if (sort.key !== key) return <ArrowUpDown className='h-3 w-3 opacity-40' />;
    return sort.dir === 'asc' ? <ArrowUp className='h-3 w-3' /> : <ArrowDown className='h-3 w-3' />;
  };

  return (
    <div className='rounded-md border bg-muted/50'>
      <button
        type='button'
        onClick={onToggle}
        aria-expanded={open}
        className='flex w-full items-center gap-2 px-3 py-2 text-left text-sm'
      >
        <Info className='h-4 w-4 flex-shrink-0 text-muted-foreground' />
        <span className='flex-1'>
          <span className='font-medium'>
            {entries.length} {noun} allocated to other tenants
          </span>
          <span className='ml-1 text-muted-foreground'>
            — unassign there first to move them here
          </span>
        </span>
        <span className='text-xs text-muted-foreground'>{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div className='border-t'>
          {rows.length > 5 && (
            <div className='border-b p-2'>
              <div className='relative'>
                <Search
                  className='absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2
                    text-muted-foreground'
                />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder='Search zone, tenant or OEM…'
                  className='h-8 pl-7'
                  aria-label='Search zones allocated to other tenants'
                />
              </div>
            </div>
          )}

          {/* Bounded so the dialog height never depends on how many zones are
              blocked; the sticky header keeps the columns labelled while scrolling. */}
          <div className='max-h-[220px] overflow-y-auto'>
            {visibleRows.length === 0 ? (
              <p className='px-3 py-4 text-center text-sm text-muted-foreground'>
                No zone or tenant matches “{search.trim()}”.
              </p>
            ) : (
              <Table>
                <TableHeader className='sticky top-0 z-10 bg-muted'>
                  <TableRow>
                    <SortableHead
                      label='Zone'
                      sortKey='zone'
                      onSort={toggleSort}
                      indicator={sortIndicator('zone')}
                      active={sort.key === 'zone'}
                      dir={sort.dir}
                    />
                    <SortableHead
                      label='Assigned to'
                      sortKey='tenant'
                      onSort={toggleSort}
                      indicator={sortIndicator('tenant')}
                      active={sort.key === 'tenant'}
                      dir={sort.dir}
                    />
                    <SortableHead
                      label='Assigned since'
                      sortKey='since'
                      onSort={toggleSort}
                      indicator={sortIndicator('since')}
                      active={sort.key === 'since'}
                      dir={sort.dir}
                    />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleRows.map(({ zone, owner, assignedAt }) => (
                    <TableRow key={`${owner.id}-${zone.id}`}>
                      <TableCell className='py-1.5'>
                        <div className='flex items-center gap-2'>
                          {/* Lock + the owner column carry the meaning; dimming
                              alone would fail WCAG 1.4.1 (never colour only). */}
                          <Lock className='h-3.5 w-3.5 flex-shrink-0 text-muted-foreground' />
                          <span className='truncate'>{zone.zone_name}</span>
                          <Badge
                            variant='outline'
                            className={cn('flex-shrink-0 text-xs', getOemTheme(zone.oem).className)}
                          >
                            {zone.oem}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className='py-1.5 font-medium'>{owner.tenant_name}</TableCell>
                      <TableCell className='py-1.5 tabular-nums text-muted-foreground'>
                        {formatAssignedSince(assignedAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface SortableHeadProps {
  label: string;
  sortKey: SortKey;
  onSort: (key: SortKey) => void;
  indicator: React.ReactNode;
  active: boolean;
  dir: 'asc' | 'desc';
}

function SortableHead({ label, sortKey, onSort, indicator, active, dir }: SortableHeadProps) {
  return (
    <TableHead
      // aria-sort is what tells a screen reader the table is sorted and how.
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className='h-8'
    >
      <button
        type='button'
        onClick={() => onSort(sortKey)}
        className='flex items-center gap-1 text-xs font-medium hover:text-foreground'
      >
        {label}
        {indicator}
      </button>
    </TableHead>
  );
}

export default ZoneAssignmentPanel;
